import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';

/** Kant van een nodale lijn: design ("Waar we zijn") of personaliteit ("Wat we zien"). */
export type HdNodalLineSide = 'design' | 'personality';

/** De zes lijnen in cheat-sheet-volgorde. */
export const HD_NODAL_LINES: readonly number[] = [1, 2, 3, 4, 5, 6];

/** Figma-kleuren: design-kaart bruin, personaliteits-kaart donkergrijs. */
const HD_NODAL_LINE_COLORS: Readonly<Record<HdNodalLineSide, string>> = {
  design: '#603535',
  personality: '#3d414e',
};

/** Scope-keys van de kicker ("Design"/"Personaliteit") en de subtitel per kant. */
const HD_NODAL_LINE_KICKER_KEYS: Readonly<Record<HdNodalLineSide, string>> = {
  design: 'designTitle',
  personality: 'personalityTitle',
};
const HD_NODAL_LINE_SUBTITLE_KEYS: Readonly<Record<HdNodalLineSide, string>> = {
  design: 'whereLabel',
  personality: 'seeLabel',
};

/** Eén design- of personaliteits-kaart van een nodale lijn in de content-taal. */
interface NodalLineCardVm {
  readonly color: string;
  /** "Design" of "Personaliteit". */
  readonly kicker: string;
  readonly name: string;
  readonly keyword: string;
  readonly keyword2: string;
  /** "Waar we zijn" (design) of "Wat we zien" (personaliteit). */
  readonly subtitle: string;
  readonly bullets: readonly string[];
  readonly meta: string;
}

/**
 * Eén kaart uit de Nodal lines cheat sheet (Figma "A3 HD Nodal Lines"): de
 * design-kaart ("Waar we zijn": de omgeving/het pad) of de personaliteits-kaart
 * ("Wat we zien": het perspectief op die omgeving) van één nodale lijn.
 *
 * Gedeeld door de cheat-sheet-pagina (alle 12 kaarten) en het nodale-
 * omgevingen-paneel (de twee lijnen van het eigen levenskruis). Content komt
 * uit de `human-design/nodal-lines`-scope; nog niet vertaalde EN-velden vallen
 * terug op NL.
 */
@Component({
  selector: 'hd-nodal-line-card',
  standalone: true,
  templateUrl: './hd-nodal-line-card.component.html',
  styleUrl: './hd-nodal-line-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdNodalLineCardComponent {
  readonly line = input.required<number>();
  readonly side = input.required<HdNodalLineSide>();

  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly card = computed<NodalLineCardVm>(() => {
    const line = this.line();
    const side = this.side();
    const lang = this.contentLang.effectiveLang();
    return {
      color: HD_NODAL_LINE_COLORS[side],
      kicker: this.text(HD_NODAL_LINE_KICKER_KEYS[side], lang),
      name: this.text(`lines.${line}.name`, lang),
      keyword: this.text(`lines.${line}.keyword`, lang),
      keyword2: this.text(`lines.${line}.keyword2`, lang),
      subtitle: this.text(HD_NODAL_LINE_SUBTITLE_KEYS[side], lang),
      bullets: this.list(`${side}.${line}`, lang),
      meta: this.text(`lines.${line}.meta`, lang),
    };
  });

  constructor() {
    // Beide content-talen eager laden zodat de taalwissel én de NL-fallback
    // meteen correcte tekst opleveren (guarded: herhaalde aanroepen zijn gratis).
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  /** Scope-tekst in de content-taal, met NL-fallback voor (nog) lege EN-velden. */
  private text(key: string, lang: string): string {
    const value = this.gateInfo.cheatSheetTextIn('nodalLines', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('nodalLines', key, 'nl');
  }

  /** Scope-string-array in de content-taal, met dezelfde NL-fallback als {@link text}. */
  private list(key: string, lang: string): readonly string[] {
    const value = this.gateInfo.cheatSheetListIn('nodalLines', key, lang);
    if (value.length > 0 || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetListIn('nodalLines', key, 'nl');
  }
}
